import { ColDef } from "ag-grid-community";
import { AgGridReactProps } from "ag-grid-react";
import { Button, Card, Col, message, Row } from "antd";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import React, { useState } from "react";
import Grid from "../../../../atoms/Grid";
import { convertToTitleCase, formatCurrency } from "../../../../libs/utils";
import { ButtonGroup } from "../../../../molecules/Button/ButtonGroup";
import { usePendingRequestsActions } from "../../../PendingApprovalRequets/api/useRefreshPendingRequests";
import { useInvoiceActionNavigation } from "../../hooks/useInvoiceActionNavigation";
import { useApprovePaymentReceipt } from "./api";
import { RejectApproval } from "./RejectApproval";

interface ChequeRow {
  id: string;
  receiptNo: string;
  chequeNo: string;
  chequeDate: string;
  amount: number;
  data?: any;
  totalAmount?: number;
  fullAmount?: number;
  amountToPay?: number | string;
  paymentMethodType?: string;
}

interface ReceiptConfirmationProps {
  data: any;
  onSuccess: () => void;
  setVisible: (value: { status: boolean; index: number }) => void;
  /** After approve/reject from a deep link, navigate here (default: invoicing). */
  successRedirectPath?: string;
  isReceiptPage?: boolean;
}
interface ApprovePayload {
  leaseId?: string;
  pdcId?: string;
  subPdcId?: string;
  approvalStatus: "approved" | "rejected";
  approvalType?: string;
  reason?: string;
  paymentReceiptId: string;
  isReceipt?: boolean;
}

/** Cheque date on the instrument; deposit date is a separate bank-deposit field. */
const resolveChequeDisplayDate = (record: any, preferDepositDate: boolean) => {
  const chequeDate =
    record?.chequeDate ?? record?.paymentDetails?.chequeDate ?? "";
  const depositDate = record?.depositDate ?? record?.paymentDetails?.depositDate ?? "";
  if (preferDepositDate) return depositDate || chequeDate;
  return chequeDate || depositDate;
};

export const ReceiptConfirmation = ({
  data,
  onSuccess,
  setVisible,
  successRedirectPath = "/invoicing",
  isReceiptPage = false,
}: ReceiptConfirmationProps) => {
  const [selectedRecord, setSelectedRecord] = useState<{
    paymentReceiptId?: string;
    pdcId?: string;
    leaseId?: string;
    subPdcId?: string;
  } | null>(null);

  //  for refetching the pending requests after approval/rejection and showing the updated count in the drawer header without refreshing the page
  const { refreshPendingRequests } = usePendingRequestsActions();
  const { goTo, pdcId: serachParampdcId } = useInvoiceActionNavigation();

  const [rejectApproval, setRejectApproval] = useState(false);
  const { approvePaymentReceipt, loading } = useApprovePaymentReceipt();
  let isPartial =
    Array.isArray(data?.params?.data?.receiptApprovals) && data?.params?.data?.receiptApprovals?.length > 0;

  const chequeRowData = isPartial ? data?.params?.data?.receiptApprovals : [data?.params?.data];

  const preferDepositDateForChequeColumn = !isReceiptPage;
  const filterCheques =
    chequeRowData.map((pdc: any) => ({
      ...pdc,
      totalAmount: pdc?.totalAmount,
      chequeStatus: pdc?.status || "",
      modeOfPayment: pdc?.modeOfPayment,
      chequeDate: resolveChequeDisplayDate(pdc, preferDepositDateForChequeColumn),
      invoiceNo: pdc?.invoiceNo || data?.params?.data?.invoiceNumber,
    })) || [];

  const chequeColumnDefs: ColDef<ChequeRow>[] = [
    {
      headerCheckboxSelection: false,
      // not sure what api will return that's why adding all possible status
      checkboxSelection: (params: any) => params.data?.status !== "paid" && params.data?.status !== "rejected",
      width: 50,
      flex: 1,
    },
    {
      headerName: "Invoice No",

      field: "invoiceNo",
      maxWidth: 150,
      width: 150,
      cellRendererFramework: ({ data }) => convertToTitleCase(data?.invoiceNo) || "--",
    },

    {
      headerName: "Payment Mode",
      field: "modeOfPayment",
      maxWidth: 150,
      width: 150,
      cellRendererFramework: ({ data }) => (convertToTitleCase(data?.modeOfPayment) ?? data?.paymentMode) || "--",
    },

    {
      headerName: "Cheque No",
      field: "documentNo",
      minWidth: 140,
      width: 140,
    },
    {
      headerName: "Bank Name",
      field: "bankName",
      maxWidth: 200,
      width: 200,
    },
    {
      headerName: "Cheque Date",
      field: "chequeDate",
      minWidth: 140,
      width: 140,
      valueFormatter: (params) => (params.value ? dayjs(params.value).format("DD-MM-YYYY") : ""),
    },
    {
      headerName: "Amount",
      maxWidth: 150,
      width: 150,
      type: "numericColumn",
      valueGetter: (params) => {
        const amountToPay = Number(params.data?.amountToPay ?? 0);
        if (params.data?.paymentMethodType === "full" && amountToPay === 0) return Number(params.data?.fullAmount ?? 0);
        return amountToPay || Number(params.data?.totalAmount ?? 0);
      },
      valueFormatter: ({ value }) => formatCurrency(value),
    },
    {
      headerName: "Status",
      maxWidth: 200,
      width: 200,
      field: "chequeStatus",
      cellRendererFramework: ({ data }) => convertToTitleCase(data?.chequeStatus) || "--",
    },
  ];

  const onSelectionChanged: AgGridReactProps["onSelectionChanged"] = ({ api: _api }) => {
    if (isPartial) {
      setSelectedRecord({
        paymentReceiptId: _api?.getSelectedRows()[0]?.approvalId,
        pdcId: data?.params?.data?._id,
        subPdcId: _api?.getSelectedRows()[0]?.subPdcId,
        leaseId: data?.params?.data?.leaseId,
      });
    } else if (isReceiptPage) {
      setSelectedRecord({
        paymentReceiptId: _api?.getSelectedRows()[0]?.paymentReceiptId,
      });
    } else {
      setSelectedRecord({
        paymentReceiptId: _api?.getSelectedRows()[0]?.receiptApprovals[0]?.approvalId,
        pdcId: _api?.getSelectedRows()[0]?.paymentReceipts[0]?.pdcId,
        leaseId: _api?.getSelectedRows()[0]?.paymentReceipts[0]?.leaseId,
      });
    }
  };

  const handleApprove = async (reason?: string) => {
    try {
      if (isReceiptPage) {
        if (!selectedRecord?.paymentReceiptId) {
          message.error("Please select a record first");
          return;
        }
      } else {
        if (!selectedRecord?.leaseId || !selectedRecord?.pdcId || !selectedRecord?.paymentReceiptId) {
          message.error("Please select a record first");
          return;
        }
      }

      let payload: ApprovePayload = isReceiptPage
        ? {
            paymentReceiptId: selectedRecord.paymentReceiptId,
            approvalStatus: rejectApproval ? "rejected" : "approved",
            approvalType: rejectApproval ? "rejectReceipt" : "approveReceipt",
            ...(reason && rejectApproval && { reason }),
            isReceipt: isReceiptPage,
          }
        : {
            leaseId: selectedRecord.leaseId,
            pdcId: selectedRecord.pdcId,
            paymentReceiptId: selectedRecord.paymentReceiptId,
            subPdcId: selectedRecord.subPdcId,
            approvalStatus: rejectApproval ? "rejected" : "approved",
            ...(reason && rejectApproval && { reason }),
            approvalType: rejectApproval ? "rejectPaymentReceipt" : "approvePaymentReceipt",
          };

      let result = await approvePaymentReceipt(payload);

      if (result?.success) {
        setSelectedRecord(null);

        await refreshPendingRequests();

        message.success(rejectApproval ? "Rejected successfully" : "Approved successfully");
        onSuccess();
        serachParampdcId && goTo(successRedirectPath, { replace: true });
      }
    } catch (err) {
      if (isAxiosError(err)) {
        message.error(err?.response?.data?.message || "Failed to update");
      }
    }
  };

  return (
    <Card bodyStyle={{ padding: 0 }}>
      {rejectApproval && (
        <RejectApproval loading={loading} handleApprove={handleApprove} setRejectApproval={setRejectApproval} />
      )}
      {!rejectApproval && (
        <>
          <Grid
            data={
              isPartial
                ? filterCheques
                : chequeRowData.map((pdc: any) => ({
                    ...pdc,
                    chequeDate: resolveChequeDisplayDate(pdc, preferDepositDateForChequeColumn),
                  }))
            }
            columns={chequeColumnDefs}
            className="pl-0 pr-0"
            onSelectionChanged={onSelectionChanged}
            getRowId={(params) => params.data.id}
          />

          <Row
            gutter={24}
            justify="space-between"
            style={{ marginTop: "10px", marginBottom: "15px", marginLeft: "5px", marginRight: "5px" }}
          >
            <Col>
              <Button className="ef-discard-btn" onClick={() => setVisible({ status: false, index: 0 })}>
                Back
              </Button>
            </Col>
            <Col>
              <ButtonGroup
                primaryButton={{
                  label: "Approve",
                  loading,
                  disabled: !selectedRecord?.paymentReceiptId,
                  onClick: () => handleApprove(),
                }}
                secondaryButton={{
                  label: "Reject",
                  disabled: !selectedRecord?.paymentReceiptId || loading,
                  onClick: () => setRejectApproval(true),
                }}
              />
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
};
